import { RECAPTCHA_SECRET } from "@constants/constants";
import { commonsApiBaseUrl, onboardApiBaseUrl } from "@constants/urlConstants";
import { encryptPayload } from "./encryptPayload";

declare global {
    interface Window {
        grecaptcha?: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

const computeApiVersion = (url: string) => {
    if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
        url = window.origin
    }
    else {
        /* do nothing */
    }
    /*--------------- check version here ----------------- */
    if (url.includes("rsh.lumenore.com")) {
        return "rsh"
    }
    else if (url.includes("qa.lumenore.com")) {
        return "qa"
    }
    else {
        return ""
    }
}

const getResponseHeaders = (response: Response) => {
    const responseHeaders: Record<string, string> = {};
    for (const [key, value] of (response?.headers?.entries?.() || [])) {
        responseHeaders[key] = value;
    }
    return responseHeaders;
}

async function parseXmlResponse<T>(response: Response): Promise<T> {
    const text = await response.text();
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, 'application/xml');
    return xml as unknown as T;
}

async function parseUnknownResponse<T>(response: Response): Promise<T> {
    const text = await response.text();
    return text as unknown as T;
}

async function getParsedResponse<T>(response: Response) {
    const contentType = response.headers.get('content-type') || '';
    switch (true) {
        case contentType.includes('application/json'): {
            const parsedResponse = await response.json();
            const responseHeaders = getResponseHeaders(response);
            return ({ ...parsedResponse, responseHeaders }) as T;
        }
        case contentType.includes('text/plain'):
        case contentType.includes('text/html'):
            return (await response.text()) as T;
        case contentType.includes('application/xml'):
        case contentType.includes('text/xml'):
            return await parseXmlResponse<T>(response);
        case contentType.includes('application/octet-stream'):
            return (await response.arrayBuffer()) as unknown as T;
        default: return await parseUnknownResponse<T>(response);
    }
}

const checkIsServer = () => {
    return typeof window === "undefined";
}

const getCredentials = () => {
    return checkIsServer() ? "omit" : "include";
}

const getParentDomain = () => {
    const hostname = window.location.hostname;
    const domainParts = hostname.split(".");
    if (domainParts.length >= 2) {
        return domainParts.slice(-2).join(".")
    }
    else {
        return ""
    }
}

const setAllowedCookiesOnly = () => {
    if (checkIsServer()) {
        /* do not change cookies */
    }
    else if (typeof document !== "undefined") {
        const whiteListedCookiesList = ["access_token", "orgName", "sessionId", "signup_source", "locale", "token"];
        const whiteListedCookiesPrefixList = ["embed_", "sso_access_token_"];
        const allCookies = document.cookie.split(";").map(c => c.trim());
        const parentDomain = getParentDomain();

        allCookies.forEach(cookie => {
            const [name, value] = cookie.split("=");
            const keepCookie = ((whiteListedCookiesList.includes(name) || whiteListedCookiesPrefixList.some(prefix => name.startsWith(prefix))) && value !== "");
            if (keepCookie) {
                /* do nothing */
            }
            else {
                document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                if (parentDomain) {
                    document.cookie = `${name}=; Path=/; Domain=.${parentDomain}; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
                }
                else {
                    /* do nothing */
                }
            }
        });
    }
    else {
        /* do nothing */
    }
}

const computeHeaders = (url: string, token?: string, extraHeaders: Record<string, string> = {}) => {
    setAllowedCookiesOnly();
    const headers: Record<string, string> = {
        ...token ? { Authorization: token.startsWith("Bearer") ? token : `Bearer ${token}` } : {},
        ...extraHeaders,
        'Content-Type': 'application/json',
        'Accept-Language': 'en-US,en;q=0.9',
        version: computeApiVersion(url)
    };
    return headers
}

const addQueryParamToUrl = (url: string, key: string, value: string): string => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${key}=${value}`;
};

const getRecaptchaToken = (token?: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (token?.trim?.()?.length) {
            resolve("");
        }
        else if (typeof window !== 'undefined' && 'grecaptcha' in window) {
            window?.grecaptcha?.ready?.(async () => {
                try {
                    const token = await window?.grecaptcha?.execute?.(RECAPTCHA_SECRET, { action: 'submit' }) || "";
                    resolve(token);
                }
                catch (error) {
                    console.error('Recaptcha execution error:', error);
                    reject(new Error('Recaptcha execution error'));
                }
            });
        }
        else {
            resolve("");
        }
    })
}

export const getRequestUrlAndRequestDataWithRecaptchaToken = async (url: string, data: NestedObject, methodType: string, token?: string) => {
    let requestUrl = url;
    const requestData = data;
    const recaptchaToken = await getRecaptchaToken(token);
    if (recaptchaToken?.trim?.()?.length) {
        if (["GET", "DELETE"].includes(methodType.toUpperCase())) {
            requestUrl = addQueryParamToUrl(requestUrl, "recaptchaToken", recaptchaToken);
        }
        else if (requestData?.data) {
            (requestData.data as NestedObject).recaptchaToken = recaptchaToken;
        }
        else {
            requestData["recaptchaToken"] = recaptchaToken;
        }
    }
    else {
        /* do nothing */
    }
    return { requestUrl, requestData }
}

export const checkIfUrlSupportsEncryption = (url: string) => {
    return (url.startsWith(commonsApiBaseUrl) || url.startsWith(onboardApiBaseUrl));
}

const makeRequest = async <T>(url: string, data: NestedObject = {}, methodType: "GET" | "POST" | "PUT" | "DELETE" = "POST", token?: string, extraHeaders: Record<string, string> = {}): Promise<T> => {
    const { requestUrl, requestData } = await getRequestUrlAndRequestDataWithRecaptchaToken(url, data, methodType, token);

    let body: BodyInit | null = null;
    let headers = computeHeaders(url, token, extraHeaders);

    if (methodType !== 'GET' && methodType !== 'DELETE') {
        if (!checkIsServer() && checkIfUrlSupportsEncryption(url)) {
            body = await encryptPayload(requestData) as BodyInit;
            headers = {
                ...headers,
                "Content-Type": "application/octet-stream",
                "X-REQUEST-ENCRYPT": "true"
            };
        } else {
            body = JSON.stringify(requestData);
        }
    }


    const options: RequestInit = {
        method: methodType,
        headers: headers,
        body: body,
        credentials: getCredentials()
    };
    const response = await fetch(requestUrl, options);
    if (response?.ok) {
        return await getParsedResponse<T>(response)
    }
    else {
        throw await getParsedResponse<T>(response)
    }
};

export default makeRequest;